/**
 * Embed Code Generator
 * Generates iframe code for white-label job board embedding
 */

import React, { useState, useMemo } from 'react';
import { toast } from 'react-hot-toast';

interface EmbedCodeGeneratorProps {
  companyId: string;
  companyName: string;
}

const EmbedCodeGenerator: React.FC<EmbedCodeGeneratorProps> = ({
  companyId,
  companyName
}) => {
  const [config, setConfig] = useState({
    brandColor: '#6366f1',
    logoUrl: '',
    width: '100%',
    height: '800px',
  });
  
  const [showPreview, setShowPreview] = useState(false);
  const [previewKey, setPreviewKey] = useState(0);

  // Memoize URLs to prevent iframe reload on every render
  const baseUrl = useMemo(() => window.location.origin, []);
  
  const embedUrl = useMemo(() => 
    `${baseUrl}/careers?company=${companyId}&color=${encodeURIComponent(config.brandColor)}&name=${encodeURIComponent(companyName)}&logo=${encodeURIComponent(config.logoUrl)}&embedded=true`,
    [baseUrl, companyId, config.brandColor, companyName, config.logoUrl]
  );

  const iframeCode = useMemo(() => 
    `<iframe
  src="${embedUrl}"
  width="${config.width}"
  height="${config.height}"
  frameborder="0"
  scrolling="yes"
  style="border: none; display: block;"
  title="${companyName} Careers"
></iframe>`,
    [embedUrl, config.width, config.height, companyName]
  );

  const copyToClipboard = () => {
    navigator.clipboard.writeText(iframeCode);
    toast.success('Embed code copied to clipboard!');
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <h3 className="text-xl font-semibold text-gray-900 mb-4">
        🌐 Embed Job Board on Your Website
      </h3>
      
      <p className="text-gray-600 mb-6">
        Add a white-label careers portal to your company website. Customize the appearance
        to match your brand and start receiving applications directly.
      </p>

      {/* Configuration */}
      <div className="space-y-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Brand Color
            </label>
            <div className="flex gap-2">
              <input
                type="color"
                value={config.brandColor}
                onChange={(e) => setConfig({ ...config, brandColor: e.target.value })}
                className="h-10 w-20 rounded border border-gray-300"
              />
              <input
                type="text"
                value={config.brandColor}
                onChange={(e) => setConfig({ ...config, brandColor: e.target.value })}
                placeholder="#6366f1"
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Logo URL (optional)
            </label>
            <input
              type="url"
              value={config.logoUrl}
              onChange={(e) => setConfig({ ...config, logoUrl: e.target.value })}
              placeholder="https://yourcompany.com/logo.png"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Width
            </label>
            <input
              type="text"
              value={config.width}
              onChange={(e) => setConfig({ ...config, width: e.target.value })}
              placeholder="100%"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg"
            />
            <p className="text-xs text-gray-500 mt-1">e.g., 100%, 1200px</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Height
            </label>
            <input
              type="text"
              value={config.height}
              onChange={(e) => setConfig({ ...config, height: e.target.value })}
              placeholder="800px"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg"
            />
            <p className="text-xs text-gray-500 mt-1">e.g., 800px, 100vh</p>
          </div>
        </div>
      </div>

      {/* Preview */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-sm font-medium text-gray-700">Preview</h4>
          <button
            onClick={() => {
              setShowPreview(!showPreview);
              if (!showPreview) setPreviewKey(prev => prev + 1);
            }}
            className="text-sm px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
          >
            {showPreview ? 'Hide Preview' : 'Show Preview'}
          </button>
        </div>
        {showPreview ? (
          <>
            <div className="border border-gray-300 rounded-lg overflow-hidden" style={{ height: '400px' }}>
              <iframe
                key={previewKey}
                src={embedUrl}
                width="100%"
                height="100%"
                frameBorder="0"
                title="Preview"
                sandbox="allow-same-origin allow-scripts allow-forms"
              />
            </div>
            <p className="text-xs text-gray-500 mt-2">
              💡 Click "Show Preview" again to refresh with new settings
            </p>
          </>
        ) : (
          <div className="border border-gray-300 rounded-lg bg-gray-50 flex items-center justify-center" style={{ height: '400px' }}>
            <div className="text-center text-gray-500">
              <svg className="w-16 h-16 mx-auto mb-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
              <p className="font-medium">Click "Show Preview" to load</p>
              <p className="text-sm mt-1">Preview will only load when you click the button</p>
            </div>
          </div>
        )}
      </div>

      {/* Embed Code */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-sm font-medium text-gray-700">Embed Code</h4>
          <button
            onClick={copyToClipboard}
            className="text-sm px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Copy Code
          </button>
        </div>
        <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto text-sm">
          <code>{iframeCode}</code>
        </pre>
      </div>

      {/* Instructions */}
      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="font-medium text-blue-900 mb-2">📋 Integration Instructions</h4>
        <ol className="list-decimal list-inside space-y-2 text-sm text-blue-800">
          <li>Copy the embed code above</li>
          <li>Paste it into your website's HTML where you want the careers portal to appear</li>
          <li>Common places: /careers, /jobs, or /work-with-us pages</li>
          <li>The job board will automatically update when you post new positions</li>
          <li>Applications will flow directly into your recruitment pipeline</li>
        </ol>
      </div>

      {/* Direct Link */}
      <div className="mt-6">
        <h4 className="text-sm font-medium text-gray-700 mb-2">Direct Link (No Embed)</h4>
        <div className="flex gap-2">
          <input
            type="text"
            value={embedUrl.replace('&embedded=true', '')}
            readOnly
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg bg-gray-50"
          />
          <button
            onClick={() => {
              navigator.clipboard.writeText(embedUrl.replace('&embedded=true', ''));
              toast.success('Link copied!');
            }}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
          >
            Copy
          </button>
        </div>
        <p className="text-xs text-gray-500 mt-1">
          Share this link on social media, emails, or anywhere else
        </p>
      </div>

      {/* Benefits */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="text-center p-4 bg-gradient-to-br from-green-50 to-green-100 rounded-lg">
          <div className="text-3xl mb-2">💰</div>
          <h5 className="font-semibold text-gray-900 mb-1">Save Money</h5>
          <p className="text-sm text-gray-600">No expensive job board fees</p>
        </div>
        
        <div className="text-center p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg">
          <div className="text-3xl mb-2">🎨</div>
          <h5 className="font-semibold text-gray-900 mb-1">Your Brand</h5>
          <p className="text-sm text-gray-600">Fully customizable appearance</p>
        </div>
        
        <div className="text-center p-4 bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg">
          <div className="text-3xl mb-2">⚡</div>
          <h5 className="font-semibold text-gray-900 mb-1">Instant Updates</h5>
          <p className="text-sm text-gray-600">Jobs sync automatically</p>
        </div>
      </div>
    </div>
  );
};

export default EmbedCodeGenerator;
